import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Bot, Loader2, Link2, RefreshCw, ExternalLink } from "lucide-react";
import { ReceiptSourcesDialog } from "@/components/inbox/ReceiptSourcesDialog";
import { PortalQuickLogin } from "@/components/PortalQuickLogin";

/** Ein erkanntes Portal (z.B. Digitec) – verbunden: Status + Schnell-Login; sonst Direktlink + «verbinden». */
function PortalRow({ companyId, id, p, requested, onConnect }) {
  if (p.connection_id) {
    return (
      <span className="basis-full flex items-center gap-2 flex-wrap" data-testid={`bank-tx-portal-row-${p.key}-${id}`}>
        <span className="font-medium text-slate-700">{p.name}:</span>
        <span className="text-slate-600" data-testid={`bank-tx-portal-status-${id}`}>
          {requested ? `Abruf gestartet ${requested}${p.last_status === "running" ? " · läuft …" : ""}` : "wird bei jedem Bank-Import automatisch abgerufen"}
        </span>
        {p.last_status === "error" && p.last_message && (
          <span className="basis-full text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid={`bank-tx-portal-error-${id}`}>
            Letzter Abruf fehlgeschlagen: {p.last_message}
          </span>
        )}
        <PortalQuickLogin companyId={companyId} connectionId={p.connection_id} />
      </span>
    );
  }
  return (
    <span className="basis-full flex items-center gap-2 flex-wrap" data-testid={`bank-tx-portal-row-${p.key}-${id}`}>
      <span className="font-medium text-slate-700">{p.name}:</span>
      {p.invoices_url && (
        <a href={p.invoices_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 font-medium text-indigo-800 hover:bg-indigo-100"
          data-testid={`bank-tx-portal-open-${p.key}-${id}`}>
          <ExternalLink className="h-3 w-3" /> Rechnungsseite öffnen
        </a>
      )}
      <button type="button" onClick={(e) => { e.stopPropagation(); onConnect(p.key); }} className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 underline underline-offset-2 font-medium" data-testid={`bank-tx-portal-connect-${p.key}-${id}`}>
        <Link2 className="h-3 w-3" /> verbinden (nur E-Mail + Passwort)
      </button>
    </span>
  );
}

/** Bankbewegung von Netflix & Co.: Beleg automatisch beim Portal abrufen oder Portal verbinden. */
export function BankPortalFetchHint({ companyId, tx, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [connectPreset, setConnectPreset] = useState(null);
  const h = tx.portal_hint;
  if (!h || tx.matched) return null;
  const id = tx.journal_entry_id;
  const portals = h.portals?.length ? h.portals : [h];
  const anyConnected = portals.some((p) => p.connection_id);

  const fetchNow = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/bank-transactions/${id}/fetch-portal-receipt`);
      toast.success(`Roboter gestartet: sucht den Beleg bei ${r.data.portal} – landet automatisch im Posteingang`);
      onChanged?.();
    } catch (err) {
      const raw = err.response?.data?._rawDetail;
      if (raw?.error === "no_connection") setConnectPreset(portals[0].key);
      else toast.error(err.response?.data?.detail || "Abruf fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const requested = h.fetch_requested_at ? h.fetch_requested_at.slice(0, 16).replace("T", " ") : null;

  return (
    <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" data-testid={`bank-tx-portal-${id}`} onClick={(e) => e.stopPropagation()}>
      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 px-2 py-0.5 font-medium" data-testid={`bank-tx-portal-badge-${id}`}>
        <Bot className="h-3 w-3" /> {portals.map((p) => p.name).join(" / ")} erkannt
      </span>
      {portals.length > 1 && !anyConnected && (
        <span className="text-slate-500">Der Beleg liegt in einem der beiden Shops:</span>
      )}
      {portals.map((p) => (
        <PortalRow key={p.key} companyId={companyId} id={id} p={p} requested={p.connection_id ? requested : null} onConnect={setConnectPreset} />
      ))}
      {anyConnected && (
        <button type="button" onClick={fetchNow} disabled={busy} className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 underline underline-offset-2 font-medium" data-testid={`bank-tx-portal-fetch-${id}`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} {requested ? "erneut abrufen" : "jetzt abrufen"}
        </button>
      )}
      <ReceiptSourcesDialog
        open={!!connectPreset}
        onOpenChange={(o) => { if (!o) { setConnectPreset(null); onChanged?.(); } }}
        companyId={companyId}
        initialPreset={connectPreset}
        focusPortals={portals}
      />
    </div>
  );
}
